/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { CommunityBroadcast } from "@packages/sdk";
import { useRequestCommunityBroadcast, useRequestMe } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  colors,
  constantSemanticColors,
  dropShadow,
  Edit2Icon,
  IconButton,
  ModalDropdownMenu,
  ModalDropdownMenuItem,
  MoreIcon,
  Share2Icon,
  spacing,
  TrashIcon,
  useAlert,
  useConfirm,
  useModal,
} from "@packages/ui";

import {
  getBroadcastErrorDescription,
  getIsBroadcastCreator,
} from "../../../lib/utils/hallowAdminDashboard/broadcasts";
import { BroadcastEditModal } from "./BroadcastEditModal";

const dropdownMenuItemStyles: Interpolation = {
  columnGap: spacing.s,
  minWidth: "180px",
  padding: `${spacing.xs} ${spacing.s}`,
};

const dropdownMenuStyles: Interpolation = {
  boxShadow: `${dropShadow.none} ${dropShadow.ms} ${dropShadow.m} ${dropShadow.none} ${constantSemanticColors.fixedDarkenVeryLow}`,
};

const dropdownMenuItemErrorStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  color: theme.semanticColors.errorMedium,
});

const dropdownMenuItemContentStyles: Interpolation = {
  alignItems: "center",
  columnGap: spacing.s,
  display: "flex",
};

type BroadcastOptionsDropdownMenuProps = {
  broadcast: CommunityBroadcast;
  communityId: number;
};

export const BroadcastOptionsDropdownMenu = ({
  broadcast,
  communityId,
}: BroadcastOptionsDropdownMenuProps) => {
  const alert = useAlert();
  const { track } = useAnalytics();
  const t = useTranslations();
  const editModal = useModal(BroadcastEditModal);

  const { query: queryMe } = useRequestMe();

  const { mutationDelete: deleteBroadcast, mutationPut } =
    useRequestCommunityBroadcast({ communityId });

  const broadcastId = broadcast.id;

  /**
   * Edit and Send Now only apply while the broadcast is still waiting, and only
   * the creator may call them — the endpoint answers anyone else with
   * `UserNotBroadcastCreator`, so they are hidden rather than left to fail.
   * Delete stays available to every admin on purpose: a creator who loses admin
   * must not be able to strand broadcasts the others cannot stop.
   */
  const canAmend =
    Boolean(broadcast.is_scheduled) &&
    getIsBroadcastCreator({
      // The list response carries the creator as `broadcaster_user`; there is
      // no `broadcaster_user_id` on it, so reading one left this always false
      // and hid both actions from the creator.
      broadcasterUserId: broadcast.broadcaster_user?.id,
      userId: queryMe.data?.id,
    });

  const dialogDelete = useConfirm({
    cancelButton: t("general_word_cancel"),
    description: () => t("community_delete_confirmation_message"),
    primaryButton: (onClick) => (
      <Button variant="color" color={colors.red60} onClick={onClick}>
        {t("general_word_delete")}
      </Button>
    ),
    title: () => t("dashboard_broadcast_delete_title"),
  });

  const handleDelete = async () => {
    track({
      event: HallowAnalyticsEvent.TappedDeleteBroadcastMessage,
    });
    const confirm = await dialogDelete.show();
    if (!confirm) return;

    try {
      await deleteBroadcast.mutateAsync({ broadcastId });
    } catch (error) {
      alert.show({
        title: t("general_word_error"),
        description: getBroadcastErrorDescription(error, t),
      });
    }
  };

  const handleEdit = () => {
    track({
      event: HallowAnalyticsEvent.TappedEditScheduledBroadcastMessage,
    });
    editModal.show({ broadcast, communityId });
  };

  const handleSendNow = async () => {
    // Nothing stands between this tap and delivery, so a second one while the
    // first is still in flight would come back as BroadcastNotScheduled and
    // report a failure for a send that actually worked.
    if (mutationPut.isPending) return;

    track({
      event: HallowAnalyticsEvent.TappedSendNowBroadcastMessage,
    });

    try {
      // Clearing the schedule is what sends it; the text is left untouched.
      await mutationPut.mutateAsync({ broadcastId, scheduled_at: null });
    } catch (error) {
      alert.show({
        title: t("general_word_error"),
        description: getBroadcastErrorDescription(error, t),
      });
    }
  };

  return (
    <ModalDropdownMenu
      trigger={<IconButton icon={<MoreIcon />} variant="transparentNeutral" />}
      contentCSS={[dropdownMenuStyles]}
      dropdownMenuProps={{
        align: "end",
        contentProps: { collisionPadding: 20, sideOffset: 5 },
      }}
    >
      {canAmend && (
        <>
          <ModalDropdownMenuItem
            modalItemProps={{ onPointerDown: handleEdit }}
            dropdownMenuItemProps={{ onClick: handleEdit }}
            dropdownMenuItemCSS={[dropdownMenuItemStyles]}
            modalItemCSS={[dropdownMenuItemStyles]}
          >
            <div css={dropdownMenuItemContentStyles}>
              <Edit2Icon />
              {t("general_word_edit")}
            </div>
          </ModalDropdownMenuItem>
          <ModalDropdownMenuItem
            modalItemProps={{ onPointerDown: handleSendNow }}
            dropdownMenuItemProps={{ onClick: handleSendNow }}
            dropdownMenuItemCSS={[dropdownMenuItemStyles]}
            modalItemCSS={[dropdownMenuItemStyles]}
          >
            <div css={dropdownMenuItemContentStyles}>
              <Share2Icon />
              {t("dashboard_broadcast_send_now")}
            </div>
          </ModalDropdownMenuItem>
        </>
      )}
      <ModalDropdownMenuItem
        modalItemProps={{
          onPointerDown: handleDelete,
        }}
        dropdownMenuItemProps={{
          onClick: handleDelete,
        }}
        dropdownMenuItemCSS={[
          dropdownMenuItemStyles,
          dropdownMenuItemErrorStyles,
        ]}
        modalItemCSS={[dropdownMenuItemStyles, dropdownMenuItemErrorStyles]}
      >
        <div css={dropdownMenuItemContentStyles}>
          <TrashIcon />
          {t("dashboard_broadcast_delete")}
        </div>
      </ModalDropdownMenuItem>
    </ModalDropdownMenu>
  );
};

BroadcastOptionsDropdownMenu.displayName = "BroadcastOptionsDropdownMenu";
